import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { UserRole } from './ActionsPanel';
import type { Member } from '../../types/member';
import type { FinancialHealthStats } from '../../types/financialStats';
import ActionCard from './ActionCard';
import { getActions, generateNewAction, findAvailableEnglishNamedMembers } from '../../utils/actionUtils';
import { v4 as uuidv4 } from 'uuid';
import { Action } from '../../types/action';
import { FiRefreshCw, FiRotateCcw } from 'react-icons/fi';
import { usePinnedActions } from '../../contexts/PinnedActionsContext';
import { useCompletedActions } from '../../contexts/CompletedActionsContext';

// Storage key for hidden action IDs
const HIDDEN_IDS_STORAGE_KEY = 'hiddenActionIdsByRole';

interface ActionListProps {
  role: UserRole;
  members: Member[];
  financialData: Record<string, FinancialHealthStats[]>;
}

const ActionList: FC<ActionListProps> = ({ role, members, financialData }) => {
  // Get pinned actions from context
  const { 
    getActionPinsByRole, 
    togglePinAction
  } = usePinnedActions();
  
  // Get completed actions context
  const { addCompletedAction } = useCompletedActions();
  
  // Current role's pinned action IDs, retrieved from context
  const pinnedActionIds = useMemo(() => getActionPinsByRole(role), [getActionPinsByRole, role]);
  
  // Track completed actions
  const [completedActions, setCompletedActions] = useState<string[]>([]);
  
  // Track replacement actions (for refreshed ones)
  const [replacementActions, setReplacementActions] = useState<Action[]>([]);
  
  // Track base actions that should be hidden (per role)
  const [hiddenBaseActionIdsByRole, setHiddenBaseActionIdsByRole] = useState<Record<UserRole, Set<string>>>(() => {
    // Try to load from localStorage
    try {
      const storedData = localStorage.getItem(HIDDEN_IDS_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        const deserialized: Record<UserRole, Set<string>> = {
          'CEO': new Set(parsedData['CEO'] || []),
          'VP of Lending': new Set(parsedData['VP of Lending'] || []),
          'Loan Advisor': new Set(parsedData['Loan Advisor'] || [])
        };
        return deserialized;
      }
    } catch (error) {
      console.error('Error loading hidden actions from localStorage:', error);
    }
    
    // Default state if nothing in localStorage
    return {
      'CEO': new Set<string>(),
      'VP of Lending': new Set<string>(),
      'Loan Advisor': new Set<string>()
    };
  });
  
  // Current role's hidden action IDs
  const hiddenBaseActionIds = useMemo(() => hiddenBaseActionIdsByRole[role], [hiddenBaseActionIdsByRole, role]);
  
  // Save hidden IDs to localStorage when they change
  useEffect(() => {
    try {
      const serialized = {
        'CEO': Array.from(hiddenBaseActionIdsByRole['CEO']),
        'VP of Lending': Array.from(hiddenBaseActionIdsByRole['VP of Lending']),
        'Loan Advisor': Array.from(hiddenBaseActionIdsByRole['Loan Advisor'])
      };
      localStorage.setItem(HIDDEN_IDS_STORAGE_KEY, JSON.stringify(serialized));
    } catch (error) {
      console.error('Error saving hidden actions to localStorage:', error);
    }
  }, [hiddenBaseActionIdsByRole]);
  
  // Track previous actions (for undo)
  const [previousState, setPreviousState] = useState<{
    replacementActions: Action[];
    usedMemberIds: Set<string>;
    hiddenBaseActionIdsByRole: Record<UserRole, Set<string>>;
    currentRole: UserRole;
  } | null>(null);
  
  // Track used member IDs to avoid duplicates when refreshing
  const [usedMemberIds, setUsedMemberIds] = useState<Set<string>>(new Set());
  
  // Track if we're currently refreshing cards for animation purposes
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Store IDs of new cards for animations
  const [newCardIds, setNewCardIds] = useState<Set<string>>(new Set());
  
  // Log when the role changes to help with debugging
  useEffect(() => {
    console.log(`Role changed to: ${role}`);
    console.log(`Pinned action IDs for ${role}:`, Array.from(pinnedActionIds));
  }, [role, pinnedActionIds]);
  
  // Generate base actions based on role and data
  const baseActions = useMemo(() => {
    console.log(`Generating actions for ${role} with ${members.length} members available`);
    
    // Log the first few members to verify English names
    if (role === 'Loan Advisor') {
      const sampleMembers = members.slice(0, 5);
      console.log('Sample members:', sampleMembers.map(m => `${m.first_name} ${m.last_name}`));
    }
    
    const generatedActions = getActions(role, members, financialData);
    
    // Track initial member IDs
    const initialMemberIds = new Set<string>();
    generatedActions.forEach(action => {
      if (action.memberId) {
        initialMemberIds.add(action.memberId);
      }
    });
    setUsedMemberIds(initialMemberIds);
    
    return generatedActions;
  }, [role, members, financialData]);
  
  // Calculate all visible actions by combining:
  // 1. Base actions that aren't hidden (but always include pinned actions)
  // 2. Replacement actions (but ONLY those that belong to the current role)
  const visibleActions = useMemo(() => {
    // Include base actions that aren't hidden OR are pinned for current role
    const visibleBaseActions = baseActions.filter(action => 
      !hiddenBaseActionIds.has(action.id) || pinnedActionIds.has(action.id)
    );
    
    // Filter replacement actions to ONLY include those that:
    // 1. Were pinned in the current role via pinnedActionIds, OR
    // 2. Were recently generated for this role
    const visibleBaseActionIds = new Set(visibleBaseActions.map(action => action.id));
    
    // For replacementActions, consider an action visible if:
    // - It's pinned, OR
    // - It was created for this role (check applicableRoles or originalRole)
    const relevantReplacementActions = replacementActions.filter(action => 
      pinnedActionIds.has(action.id) || 
      (action.applicableRoles && action.applicableRoles.includes(role)) ||
      (action as any).originalRole === role
    );
    
    // Combine with filtered replacement actions
    // Remove any duplicate IDs (prefer replacement actions over base actions)
    const combinedActions = [...visibleBaseActions];
    
    // Add replacement actions, avoiding duplicates
    relevantReplacementActions.forEach(action => {
      if (!visibleBaseActionIds.has(action.id)) {
        combinedActions.push(action);
      }
    });
    
    return combinedActions;
  }, [baseActions, replacementActions, hiddenBaseActionIds, pinnedActionIds, role]);
  
  // Filter out completed actions to get pending actions
  const pendingActions = useMemo(() => {
    return visibleActions.filter(action => !completedActions.includes(action.id));
  }, [visibleActions, completedActions]);
  
  // Handle action completion
  const handleActionComplete = (actionId: string) => {
    // Find the action that was completed
    const action = pendingActions.find(action => action.id === actionId);
    
    if (action) {
      // Add to completed actions in the context
      addCompletedAction(action);
      
      // Update local state to filter out the completed action
      setCompletedActions(prev => [...prev, actionId]);
      
      // In a real app, we would save this to a server/database
      console.log(`Action completed: ${actionId}`);
    }
  };
  
  // Generate a new action for a specific member
  const generateNewMemberAction = useCallback((role: UserRole): Action | null => {
    // Find members we haven't used yet
    const availableMembers = findAvailableEnglishNamedMembers(members, financialData, usedMemberIds);
    
    // If no more available members, reuse an existing member
    if (availableMembers.length === 0) {
      console.log('No more new members, reusing existing members');
      
      // First try to use a hidden base action
      const hiddenBaseActionsList = baseActions.filter(action => 
        hiddenBaseActionIds.has(action.id) && !completedActions.includes(action.id)
      );
      
      if (hiddenBaseActionsList.length > 0) {
        const randomIndex = Math.floor(Math.random() * hiddenBaseActionsList.length);
        const actionToReuse = hiddenBaseActionsList[randomIndex];
        
        // Just return a copy of the action with a new ID to make it appear fresh
        return {
          ...actionToReuse,
          id: uuidv4()
        };
      }
      
      // If no hidden actions either, use any real member from our data
      if (members.length > 0) {
        const randomIndex = Math.floor(Math.random() * members.length);
        const member = members[randomIndex];
        
        // Generate new action using the actual member data
        return generateNewAction(role, member);
      }
      
      // Absolute fallback - use synthetic member with a clear name format
      return generateSyntheticAction(role);
    }
    
    // Randomly pick an available member
    const randomIndex = Math.floor(Math.random() * availableMembers.length);
    const member = availableMembers[randomIndex];
    
    // Add this member to the used list
    setUsedMemberIds(prev => new Set([...prev, member.uuid]));
    
    // Generate new action using the centralized utility function with a real member
    return generateNewAction(role, member);
  }, [members, financialData, usedMemberIds, baseActions, hiddenBaseActionIds, completedActions]);
  
  // Helper function to generate a synthetic action when we've exhausted real member data
  const generateSyntheticAction = (role: UserRole, index: number = 0): Action => {
    // Create a member ID for consistent avatar generation
    const memberId = Math.floor(Math.random() * 1000) + index;
    const actionIndex = Math.floor(Math.random() * 10) + index; // To help create variety
    
    // Generate unique title/details based on role and index
    let title, description, category, impact;
    
    if (role === 'CEO') {
      const options = [
        {
          title: `Strategic Growth Initiative ${actionIndex}`,
          description: `Review and approve new market expansion opportunity for Q${(actionIndex % 4) + 1}`,
          category: 'Strategic Planning',
          impact: { value: 240000 + (actionIndex * 10000), type: 'financial', description: 'Projected revenue growth' }
        },
        {
          title: `Executive Team Development ${actionIndex}`,
          description: `Finalize leadership training program for departmental directors`,
          category: 'Talent Management',
          impact: { value: 28 + (actionIndex % 10), type: 'engagement', description: 'Retention improvement' }
        },
        {
          title: `Digital Transformation Project ${actionIndex}`,
          description: `Review implementation timeline for core system upgrade`,
          category: 'Technology',
          impact: { value: 185000 + (actionIndex * 5000), type: 'financial', description: 'Operational savings' }
        }
      ];
      const selected = options[actionIndex % options.length];
      title = selected.title;
      description = selected.description;
      category = selected.category;
      impact = selected.impact;
    } 
    else if (role === 'VP of Lending') {
      const options = [
        {
          title: `Rate Strategy Review ${actionIndex}`,
          description: `Adjust lending rates based on current market conditions and competitive analysis`,
          category: 'Pricing Strategy',
          impact: { value: 125000 + (actionIndex * 7500), type: 'financial', description: 'Portfolio yield improvement' }
        },
        {
          title: `Underwriting Standards Update ${actionIndex}`,
          description: `Modify credit criteria for personal loan products based on risk assessment`,
          category: 'Risk Management',
          impact: { value: 85000 + (actionIndex * 3000), type: 'financial', description: 'Projected loss prevention' }
        },
        {
          title: `New Loan Product Review ${actionIndex}`,
          description: `Evaluate proposed vehicle loan promotion for summer campaign`,
          category: 'Product Development',
          impact: { value: 32 + (actionIndex % 15), type: 'engagement', description: 'Application increase' }
        }
      ];
      const selected = options[actionIndex % options.length];
      title = selected.title;
      description = selected.description;
      category = selected.category;
      impact = selected.impact;
    }
    else {
      const options = [
        {
          title: `Financial Review for Member ${memberId}`,
          description: `Schedule financial wellness checkup to review savings goals`,
          category: 'Financial Wellness',
          impact: { value: 78 + (actionIndex % 20), type: 'engagement', description: 'Satisfaction score' }
        },
        {
          title: `Loan Opportunity for Member ${memberId}`,
          description: `Present refinancing options for existing vehicle loan`,
          category: 'Refinance Opportunity',
          impact: { value: 2100 + (actionIndex * 200), type: 'financial', description: 'Member savings' }
        },
        {
          title: `Credit Builder for Member ${memberId}`,
          description: `Introduce secured credit card program to improve credit profile`,
          category: 'Credit Improvement',
          impact: { value: 45 + (actionIndex % 30), type: 'engagement', description: 'Credit score increase' }
        }
      ];
      const selected = options[actionIndex % options.length];
      title = selected.title;
      description = selected.description;
      category = selected.category;
      impact = selected.impact;
    }
    
    return {
      id: uuidv4(),
      title,
      description,
      impact: {
        type: impact.type as any,
        value: impact.value,
        description: impact.description
      },
      priority: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)] as any,
      category,
      applicableRoles: [role],
      memberId: `synthetic-${memberId}`,
      memberName: `Member ${memberId}`,
      memberPhoto: `https://i.pravatar.cc/150?u=member-${memberId}`,
      actionType: Math.random() > 0.5 ? 'semi-automated' : 'manual',
      dataSources: ['Member Data', 'Financial Profile', 'Account History'].slice(0, 2 + (actionIndex % 2)),
      steps: [
        {
          id: uuidv4(),
          title: 'Review Information',
          description: 'Review current member information and status',
          type: 'loan_review'
        },
        {
          id: uuidv4(),
          title: 'Prepare Recommendation',
          description: 'Generate personalized recommendation',
          type: 'account_update'
        },
        {
          id: uuidv4(),
          title: 'Contact Member',
          description: 'Schedule follow-up with member',
          type: 'email'
        }
      ]
    };
  };
  
  // Handle pinning/unpinning an action - now using the context
  const handleTogglePin = useCallback((actionId: string) => {
    // First check if this is a base action for the current role
    const isBaseAction = baseActions.some(action => action.id === actionId);
    
    // Find the action in either baseActions or replacementActions
    const action = baseActions.find(a => a.id === actionId) || 
                 replacementActions.find(a => a.id === actionId);
    
    if (!action) {
      console.error(`Action ${actionId} not found in baseActions or replacementActions`);
      return; // Safety check
    }
    
    // Check if pinning or unpinning (before toggle)
    const isCurrentlyPinned = pinnedActionIds.has(actionId);
    
    console.log(`${isCurrentlyPinned ? 'Unpinning' : 'Pinning'} action ${actionId}, isBaseAction: ${isBaseAction}`);
    
    // Update the pinned state in the context
    togglePinAction(role, actionId);
    
    // Only add to replacement actions if this is a base action
    // AND we're pinning the action (not unpinning)
    // AND it doesn't already exist in replacementActions
    if (!isCurrentlyPinned) { // Pinning
      // Check if this action is already in replacementActions to avoid duplicates
      const existingReplacement = replacementActions.find(a => a.id === actionId);
      
      if (!existingReplacement) {
        console.log(`Adding pinned action ${actionId} to replacement actions for persistence`);
                  
        // Clone the action with ONLY the current role in applicableRoles
        const actionToAdd = {
          ...action,
          originalRole: role, // Add this to track which role it originally belonged to
          applicableRoles: [role] // Restrict to current role only
        };
        
        setReplacementActions(prev => [...prev, actionToAdd]);
      }
    } else { // Unpinning
      // If we're unpinning a non-base action, remove it from replacementActions
      if (!isBaseAction) {
        console.log(`Removing unpinned non-base action ${actionId} from replacement actions`);
        setReplacementActions(prev => prev.filter(a => a.id !== actionId));
      }
      // If it's a base action, we don't remove it from replacementActions
      // because it might be needed later for other roles
    }
    
  }, [role, togglePinAction, pinnedActionIds, baseActions, replacementActions]);
  
  // Handle refreshing unpinned actions
  const handleRefreshUnpinned = useCallback(() => {
    // Save current state for undo
    setPreviousState({
      replacementActions: [...replacementActions],
      usedMemberIds: new Set(usedMemberIds),
      hiddenBaseActionIdsByRole: {
        'CEO': new Set(hiddenBaseActionIdsByRole['CEO']),
        'VP of Lending': new Set(hiddenBaseActionIdsByRole['VP of Lending']),
        'Loan Advisor': new Set(hiddenBaseActionIdsByRole['Loan Advisor'])
      },
      currentRole: role
    });
    
    // Identify all unpinned actions
    const unpinnedActions = pendingActions.filter(action => !pinnedActionIds.has(action.id));
    
    console.log(`Refreshing ${unpinnedActions.length} unpinned actions for ${role}`);
    
    // Generate new actions to replace the unpinned ones
    // Always generate at least 4 new actions or match the number of unpinned ones
    const numActionsToGenerate = Math.max(4, unpinnedActions.length);
    const newActions: Action[] = [];
    
    // Keep track of titles and descriptions to prevent duplicates
    const existingTitles = new Set(pendingActions.filter(action => pinnedActionIds.has(action.id)).map(action => action.title));
    const existingDescriptions = new Set(pendingActions.filter(action => pinnedActionIds.has(action.id)).map(action => action.description));
    
    for (let i = 0; i < numActionsToGenerate; i++) {
      // Try to generate a unique action
      let attempts = 0;
      let newAction: Action | null = null;
      let isDuplicate = true;
      
      while (isDuplicate && attempts < 5) {
        attempts++;
        // generateNewMemberAction will now always return an action, even if recycled
        const generatedAction = generateNewMemberAction(role);
        
        if (generatedAction) {
          // Check if this action would be a duplicate of a pinned action
          isDuplicate = existingTitles.has(generatedAction.title) || 
                        existingDescriptions.has(generatedAction.description);
          
          if (!isDuplicate) {
            newAction = generatedAction;
            // Add to tracking sets to prevent duplicates in subsequent generations
            existingTitles.add(generatedAction.title);
            existingDescriptions.add(generatedAction.description);
          }
        } else {
          // If we can't generate an action, break out
          isDuplicate = false;
        }
      }
      
      if (newAction) {
        newActions.push(newAction);
      }
    }
    
    // For each unpinned action:
    // 1. If it's a base action, hide it but don't remove it completely so it can be reused
    // 2. If it's a replacement action, remove it from replacements
    
    // Update hiddenBaseActionIds to include unpinned base actions but keep it role-specific
    const newHiddenBaseActionIds = new Set(hiddenBaseActionIds);
    const baseActionIds = new Set(baseActions.map(action => action.id));
    
    unpinnedActions.forEach(action => {
      if (baseActionIds.has(action.id)) {
        newHiddenBaseActionIds.add(action.id);
      }
    });
    
    // IMPORTANT: Never hide pinned actions, even if they are base actions
    // This ensures pinned actions always stay visible
    pinnedActionIds.forEach(pinnedId => {
      newHiddenBaseActionIds.delete(pinnedId);
    });
    
    // Update the role-specific hiddenBaseActionIds
    setHiddenBaseActionIdsByRole(prev => ({
      ...prev,
      [role]: newHiddenBaseActionIds
    }));
    
    // Only keep pinned replacement actions
    const keepReplacementActions = replacementActions.filter(action => 
      pinnedActionIds.has(action.id)
    );
    
    // Create the full set of actions - pinned + new
    const combinedActions = [...keepReplacementActions, ...newActions];
    
    // Make sure we actually have actions to display
    if (combinedActions.length === 0) {
      console.error("Error: No actions to display after refresh");
      
      // Emergency fallback - generate synthetic actions
      for (let i = 0; i < 4; i++) {
        newActions.push(generateSyntheticAction(role, i));
      }
    }
    
    // Store IDs of new cards that will be animated
    const newIds = new Set<string>(newActions.map(action => action.id));
    
    // First mark that we're refreshing (this disables animations)
    // Then update replacement actions with kept ones plus new ones
    // Then schedule animations to be enabled after a delay
    // All of these state updates need to happen in one render cycle
    setIsRefreshing(true);
    setNewCardIds(newIds);
    setReplacementActions([...keepReplacementActions, ...newActions]);
    
    // After DOM has updated, enable animations only for the new cards
    // Use a single RAF + setTimeout to ensure we don't cause unnecessary renders
    const timer = setTimeout(() => {
      setIsRefreshing(false);
    }, 100);
    
    // Clean up timer if component unmounts
    return () => clearTimeout(timer);
  }, [pendingActions, replacementActions, hiddenBaseActionIds, baseActions, pinnedActionIds, usedMemberIds, role, generateNewMemberAction, hiddenBaseActionIdsByRole]);
  
  // Handle undo operation
  const handleUndo = useCallback(() => {
    if (previousState) {
      setReplacementActions(previousState.replacementActions);
      setUsedMemberIds(previousState.usedMemberIds);
      
      // Update the hiddenBaseActionIdsByRole with previous state
      setHiddenBaseActionIdsByRole(prevState => {
        const updated = { ...prevState };
        // Convert arrays back to Sets
        const roleKey = previousState.currentRole as UserRole;
        updated[roleKey] = new Set(previousState.hiddenBaseActionIdsByRole[roleKey]);
        return updated;
      });
      
      setPreviousState(null);
      console.log(`Undid last refresh for ${previousState.currentRole}`);
    }
  }, [previousState]);
  
  // Enable controls for all roles, not just Loan Advisor
  const showControls = true;
  
  // Create a prioritized list that shows pinned actions first and ensures we always have 4 cards
  const displayedActions = useMemo(() => {
    // First, separate pinned and unpinned actions
    const pinnedActions = pendingActions.filter(action => pinnedActionIds.has(action.id));
    const unpinnedActions = pendingActions.filter(action => !pinnedActionIds.has(action.id));
    
    // Combined actions with pinned actions first
    let combinedActions = [...pinnedActions, ...unpinnedActions];
    
    // Track already included action IDs to prevent duplicates
    const includedActionIds = new Set(combinedActions.map(action => action.id));
    
    // IMPORTANT: If we have less than 4 cards total, we add more cards
    // If we have >= 4 pinned cards, we only show pinned cards
    // If we have < 4 pinned cards, we fill the rest with unpinned cards
    
    // Check if we need to add more cards (less than 4 total cards)
    if (combinedActions.length < 4) {
      // Find previously hidden base actions
      const hiddenBaseActionsList = baseActions.filter(action => 
        hiddenBaseActionIds.has(action.id) && 
        !completedActions.includes(action.id) &&
        !includedActionIds.has(action.id) // Prevent duplicates
      );
      
      // Create a pool of potential actions to reuse
      const reusePool = [...hiddenBaseActionsList];
      
      // Shuffle the reuse pool to get random cards
      const shuffledReusePool = [...reusePool].sort(() => Math.random() - 0.5);
      
      // Add cards from the reuse pool until we have 4 or run out
      for (const action of shuffledReusePool) {
        if (combinedActions.length >= 4) break;
        combinedActions.push(action);
        includedActionIds.add(action.id);
      }
    }
    
    // If we still have fewer than 4 cards, we'll just show what we have
    
    // If we have more than 4 cards but need to ensure pinned cards are always shown
    let finalCombinedActions = combinedActions;
    if (combinedActions.length > 4) {
      // Always show all pinned cards
      if (pinnedActions.length >= 4) {
        // If we have 4 or more pinned cards, only show those
        finalCombinedActions = pinnedActions;
      } else {
        // If we have fewer than 4 pinned cards, fill the remaining slots with unpinned
        const unpinnedToShow = unpinnedActions.slice(0, 4 - pinnedActions.length);
        finalCombinedActions = [...pinnedActions, ...unpinnedToShow];
      }
    }
    
    return finalCombinedActions;
  }, [pendingActions, pinnedActionIds, baseActions, hiddenBaseActionIds, completedActions]);
  
  // Show empty state if no actions
  const emptyState = (
    <div className="text-center py-8">
      <div className="bg-white/10 rounded-full p-4 inline-block mb-4">
        <svg className="w-8 h-8 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-xl font-medium text-white mb-2">All caught up!</h3>
      <p className="text-white/70 max-w-md mx-auto">
        There are no pending actions for your role at this time. Check back later for new opportunities.
      </p>
    </div>
  );
  
  return (
    <div>
      {showControls && (
        <div className="flex justify-end mb-4 gap-2">
          <button
            className="flex items-center bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-md transition-colors text-sm"
            onClick={handleRefreshUnpinned}
            title="Refresh unpinned recommendations"
          >
            <FiRefreshCw className="mr-2" />
            Refresh Unpinned
          </button>
          
          {previousState && (
            <button
              className="flex items-center bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-md transition-colors text-sm"
              onClick={handleUndo}
              title="Undo last refresh"
            >
              <FiRotateCcw className="mr-2" />
              Undo
            </button>
          )}
        </div>
      )}
      
      {pendingActions.length === 0 ? (
        emptyState
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {displayedActions.map(action => {
            // Calculate shouldAnimate:
            // 1. Never animate pinned cards during refresh
            // 2. Animate ONLY new cards when refreshing
            // 3. Animate all cards on initial render (when not refreshing)
            const isPinned = pinnedActionIds.has(action.id);
            const isNewCard = newCardIds.has(action.id);
            
            // If we're refreshing, only animate new unpinned cards
            // If not refreshing, animate all cards (initial load)
            const shouldAnimate = isRefreshing 
              ? isNewCard && !isPinned 
              : true;
              
            return (
              <ActionCard
                key={action.id}
                action={action}
                onComplete={() => handleActionComplete(action.id)}
                onTogglePin={showControls ? handleTogglePin : undefined}
                isPinned={isPinned}
                shouldAnimate={shouldAnimate}
                role={role}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionList; 